/**
 * Motion.
 *
 * The house style is Apple's: critically damped springs everywhere by default,
 * bounce reserved for motion the user's own gesture put momentum into. Motion's
 * `bounce` + `duration` spring API maps onto Apple's damping + response, so the
 * numbers below are the ones from the WWDC tables rather than invented ones.
 *
 * Nothing here uses a CSS transition for anything a finger can touch. A CSS
 * transition cannot be grabbed and reversed mid-flight, and interruptibility is
 * the single property that separates an interface that feels alive from one
 * that feels like a slideshow.
 */

import { useEffect, useState } from 'react';
import type { Transition } from 'motion/react';

/** damping 1.0, response 0.4 — reposition, appear, settle. No overshoot. */
export const springStandard: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
};

/** damping 1.0, response 0.28 — small, frequent, must feel immediate. */
export const springSnappy: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.28,
};

/**
 * damping ~0.8, response 0.3 — sheets and drawers.
 *
 * The overshoot is earned: a sheet arrives because the user flicked it, and
 * momentum that stops dead reads as hitting a wall. The same bounce on a menu
 * that merely faded in would feel wrong, which is why it is not the default.
 */
export const springSheet: Transition = {
  type: 'spring',
  bounce: 0.22,
  duration: 0.3,
};

/**
 * A gesture released with real velocity.
 *
 * Handing the release velocity to the spring is what removes the seam between
 * dragging and animating — without it the element stops dead at release and
 * starts again from rest, which reads as a stutter even when it is fast.
 *
 * Defaults match `springSheet`, since gesture-released motion is where bounce
 * is earned.
 */
export function springWithVelocity(velocity: number, bounce = 0.22): Transition {
  return { type: 'spring', bounce, duration: 0.3, velocity };
}

/**
 * Apple's momentum projection, from the *Designing Fluid Interfaces* sample.
 *
 * Note this is the exponential-decay form, not the textbook `v²/(2a)`. Snapping
 * to the boundary nearest the *release point* makes a flick feel ignored;
 * snapping to the one nearest the *projected* resting point is what makes it
 * feel thrown.
 */
export function projectMomentum(velocityPxPerSecond: number, decelerationRate = 0.998): number {
  return ((velocityPxPerSecond / 1000) * decelerationRate) / (1 - decelerationRate);
}

/**
 * Progressive resistance past a boundary.
 *
 * A hard stop reads as frozen — the user cannot tell a limit from a bug.
 * Resistance that increases with overshoot reads as "responsive, but there is
 * nothing more here", which is the truth.
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  if (dimension <= 0) return 0;
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/** Nearest snap point to a projected landing position. */
export function nearestSnapPoint(projected: number, snapPoints: readonly number[]): number {
  let best = snapPoints[0] ?? 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of snapPoints) {
    const distance = Math.abs(point - projected);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  }
  return best;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reactive, because the setting can change while the app is open — iOS exposes
 * it in Control Center, and a user who turns it on mid-session did so because
 * something was already making them uncomfortable.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/**
 * Haptics, on the frame the thing actually happens.
 *
 * Three rules from *Designing Audio-Haptic Experiences*: causality (fire on the
 * causal event, not near it), harmony (same frame as the visual — so this is
 * called synchronously from the handler, never after an await), and utility
 * (only where it earns its place). Hence four moments and no more: a commit, a
 * success, a warning, and a refusal.
 *
 * Silently absent on iOS Safari, which does not implement the Vibration API.
 * That is fine — haptics are reinforcement here, never the only signal.
 */
export type Feedback = 'commit' | 'success' | 'warning' | 'error';

const PATTERNS: Record<Feedback, number | number[]> = {
  commit: 8,
  success: [10, 40, 16],
  warning: [14, 60, 14],
  error: [22, 50, 22],
};

export function tapFeedback(kind: Feedback): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  // Reduced motion is a vestibular setting, but a user who has asked for less
  // movement has not asked for a buzzing phone either.
  if (prefersReducedMotion()) return;
  try {
    navigator.vibrate(PATTERNS[kind]);
  } catch {
    /* some browsers gate this behind engagement; silence is the right failure */
  }
}

/**
 * Velocity threshold above which a flick decides direction on its own.
 *
 * Deciding by velocity *sign* rather than by position is what lets a user flick
 * a sheet shut from anywhere on the screen instead of having to drag it past
 * an arbitrary halfway line.
 */
export const FLICK_VELOCITY = 420;

/** Movement before a drag commits to a direction. */
export const DRAG_THRESHOLD_PX = 10;
